/**
 * WCC360 — Careers spreadsheet loader (read-only, presentation only).
 *
 * `public/data/careers.csv` is the sealed workbook export. It carries the
 * numeric lifestyle signals the JSON shards only describe in prose:
 * remote-work score, work–life balance, job satisfaction, freelance
 * opportunity, growth and starting salary.
 *
 * Fetched once, parsed once, indexed by a slug of the role title — the same
 * slug the career-intel shards use, so the join is exact. Nothing here is
 * scored or written. A missing column stays null; it is never zeroed.
 */

export interface CareerSheetRow {
  wk: string | null;
  title: string;
  family: string | null;
  level: string | null;
  /** 0–100 share of employers hiring remotely. */
  remoteScore: number | null;
  /** 0–10. */
  workLifeBalance: number | null;
  /** 0–10. */
  jobSatisfaction: number | null;
  /** "Low" | "Medium" | "High" | "Very High" */
  freelance: string | null;
  demand2026: string | null;
  growthPct: number | null;
  salaryMinInr: number | null;
  salaryMaxInr: number | null;
}

const CSV_URL = "/data/careers.csv";

/** Sheet keys are role slugs. Exported so other readers key it the same way. */
export function careerSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function num(v: string | undefined): number | null {
  if (v == null) return null;
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function text(v: string | undefined): string | null {
  const t = (v ?? "").trim();
  return t ? t : null;
}

/** Minimal RFC4180 line splitter — handles quoted commas in DNA columns. */
function splitRow(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else quoted = false;
      } else cur += ch;
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

let indexPromise: Promise<Map<string, CareerSheetRow>> | null = null;

function parse(csv: string): Map<string, CareerSheetRow> {
  const map = new Map<string, CareerSheetRow>();
  const lines = csv.replace(/^\uFEFF/, "").split(/\r?\n/);
  if (!lines.length) return map;
  const head = splitRow(lines[0]).map((h) => h.replace(/^\uFEFF/, "").trim());
  const at = (row: string[], key: string) => {
    const i = head.indexOf(key);
    return i === -1 ? undefined : row[i];
  };

  for (let l = 1; l < lines.length; l++) {
    if (!lines[l].trim()) continue;
    const r = splitRow(lines[l]);
    const title = text(at(r, "Career_Title"));
    if (!title) continue;
    const slug = careerSlug(title);
    if (!slug || map.has(slug)) continue;
    map.set(slug, {
      wk: text(at(r, "Career_ID")),
      title,
      family: text(at(r, "Career_Family")),
      level: text(at(r, "Level")),
      remoteScore: num(at(r, "Remote_Work_Score")),
      workLifeBalance: num(at(r, "Work_Life_Balance_Score")),
      jobSatisfaction: num(at(r, "Job_Satisfaction_Score")),
      freelance: text(at(r, "Freelance_Opportunity")),
      demand2026: text(at(r, "Market_Demand_2026")),
      growthPct: num(at(r, "Growth_Rate_Percent")),
      salaryMinInr: num(at(r, "Starting_Salary_Min_INR")),
      salaryMaxInr: num(at(r, "Starting_Salary_Max_INR")),
    });
  }
  return map;
}

/** The whole sheet, indexed by role slug. Never throws: failure = empty map. */
export function loadCareerSheet(): Promise<Map<string, CareerSheetRow>> {
  if (!indexPromise) {
    indexPromise = fetch(CSV_URL, { cache: "force-cache" })
      .then((r) => (r.ok ? r.text() : ""))
      .then(parse)
      .catch(() => new Map<string, CareerSheetRow>());
  }
  return indexPromise;
}

/** One role's spreadsheet row, or null when the sheet doesn't list it. */
export async function loadCareerSheetRow(name: string): Promise<CareerSheetRow | null> {
  const slug = careerSlug(name);
  if (!slug) return null;
  const index = await loadCareerSheet();
  return index.get(slug) ?? null;
}

/* ---------------- display helpers ---------------- */

/** "Fully remote" / "Often remote" / "Some remote" / "On-site" from 0–100. */
export function remoteLabel(score: number | null | undefined): string | null {
  if (score == null) return null;
  if (score >= 80) return "Fully remote";
  if (score >= 60) return "Often remote";
  if (score >= 40) return "Hybrid";
  if (score >= 20) return "Some remote";
  return "On-site";
}

/** Short, honest read on a 0–10 balance score. */
export function balanceLabel(score: number | null | undefined): string | null {
  if (score == null) return null;
  if (score >= 8) return "Calm hours";
  if (score >= 6) return "Steady hours";
  if (score >= 4) return "Busy hours";
  return "Demanding hours";
}

/** Compact ₹ LPA band from the sheet's rupee figures. */
export function salaryBandLpa(
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  const lpa = (v: number) => {
    const n = v / 100000;
    return n >= 10 ? `${Math.round(n)}` : `${Math.round(n * 10) / 10}`;
  };
  if (min != null && max != null) return `₹${lpa(min)}–${lpa(max)} LPA`;
  if (min != null) return `From ₹${lpa(min)} LPA`;
  if (max != null) return `Up to ₹${lpa(max)} LPA`;
  return null;
}
